/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { TenantTypesEnum } from "Constants/TenantTypes";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IVisualStudioBenefit } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefit";

import {
  DOWNLOAD_REQUESTS_TO_CSV,
  DOWNLOAD_REQUESTS_TO_CSV_CLEAN,
  DOWNLOAD_REQUESTS_TO_CSV_FAIL,
  DOWNLOAD_REQUESTS_TO_CSV_SUCCESS,
  UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE,
  UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_CLEAR,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE,
  VISUAL_STUDIO_BENEFITS_SEARCH,
  VISUAL_STUDIO_BENEFITS_SEARCH_FAIL,
  VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS,
} from "./VisualStudioBenefitActionTypes";

/**
 * Action for when downloading visual studio
 * benefit requests.
 * @interface
 */
export interface IDownloadRequests {
  type: typeof DOWNLOAD_REQUESTS_TO_CSV;
  tenant: TenantTypesEnum;
}

/**
 * Action for when downloading visual studio
 * benefit requests.
 * @interface
 */
export interface IDownloadRequestsSuccess {
  type: typeof DOWNLOAD_REQUESTS_TO_CSV_SUCCESS;
  csvFile: any;
}

/**
 * Action for when downloading visual studio
 * benefit requests.
 * @interface
 */
export interface IDownloadRequestsFail {
  type: typeof DOWNLOAD_REQUESTS_TO_CSV_FAIL;
  errorMessage: string;
}

/**
 * Action for when after download clean the download state.
 * @interface
 */
export interface IDownloadRequestsToCSVClean {
  type: typeof DOWNLOAD_REQUESTS_TO_CSV_CLEAN;
}

/**
 * Action for when searching visual studio benefits.
 * @interface
 */
export interface ISearchVisualStudioBenefits {
  type: typeof VISUAL_STUDIO_BENEFITS_SEARCH;
  tenant: TenantTypesEnum;
  searchText: string;
  selectedBenefitsFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Action for when benefits were searched successfully.
 * @interface
 */
export interface ISearchVisualStudioBenefitsSuccess {
  type: typeof VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS;
  benefits: IVisualStudioBenefit[];
  searchPage: number;
  pageSize: number;
  filteredCount: number;
}

/**
 * Action for when loading benefits failed.
 * @interface
 */
export interface ISearchVisualStudioBenefitsFail {
  type: typeof VISUAL_STUDIO_BENEFITS_SEARCH_FAIL;
  error: string;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IUpdateCurrentPage {
  type: typeof UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IUpdatePageSize {
  type: typeof UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IVisualStudioBenefitsFilterValueAdd {
  type: typeof VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IVisualStudioBenefitsFilterValueRemove {
  type: typeof VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IVisualStudioBenefitsFilterValueUpdate {
  type: typeof VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action for when clearing selected filter values.
 * @interface
 */
export interface IVisualStudioBenefitsFilterValueClear {
  type: typeof VISUAL_STUDIO_BENEFITS_FILTER_VALUE_CLEAR;
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_SEARCH.
 */
export function searchVisualStudioBenefits(
  tenant: TenantTypesEnum,
  searchText: string,
  selectedBenefitsFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): VisualStudioBenefitActionTypes {
  return {
    type: VISUAL_STUDIO_BENEFITS_SEARCH,
    tenant,
    searchText,
    selectedBenefitsFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS.
 * @param benefits the user profiles successfully searched.
 */
export function searchVisualStudioBenefitsSuccess(
  benefits: IVisualStudioBenefit[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): ISearchVisualStudioBenefitsSuccess {
  return {
    type: VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS,
    benefits,
    searchPage,
    pageSize,
    filteredCount,
  };
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchVisualStudioBenefitsFail(error: string): ISearchVisualStudioBenefitsFail {
  return {
    type: VISUAL_STUDIO_BENEFITS_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE.
 */
export function updateCurrentPage(currentPage: number): IUpdateCurrentPage {
  return {
    type: UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE.
 */
export function updatePageSize(pageSize: number): IUpdatePageSize {
  return {
    type: UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addVisualStudioBenefitsFilterValue(
  filterValue: IFilterValue
): IVisualStudioBenefitsFilterValueAdd {
  return {
    type: VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeVisualStudioBenefitsFilterValue(
  filterValue: IFilterValue
): IVisualStudioBenefitsFilterValueRemove {
  return {
    type: VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateVisualStudioBenefitsFilterValue(
  filterValue: IFilterValue
): IVisualStudioBenefitsFilterValueUpdate {
  return {
    type: VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for DOWNLOAD_REQUESTS_TO_CSV.
 * @param searchText the search text.
 * @param selectedApplicationsFilterValues selected applications current filter values.
 * @param tenant download tenant.
 */
export function downloadRequestsToCSV(tenant: TenantTypesEnum): IDownloadRequests {
  return {
    type: DOWNLOAD_REQUESTS_TO_CSV,
    tenant,
  };
}

/**
 * Action creator for DOWNLOAD_REQUESTS_TO_CSV_FAIL.
 * @param errorMessage the error message from the API.
 */
export function downloadRequestsToCSVFail(errorMessage: string): IDownloadRequestsFail {
  return {
    type: DOWNLOAD_REQUESTS_TO_CSV_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for DOWNLOAD_REQUESTS_TO_CSV_SUCCESS.
 * @param csvFile the csv file from the API.
 */
export function downloadRequestsToCSVSuccess(csvFile: string): IDownloadRequestsSuccess {
  return {
    type: DOWNLOAD_REQUESTS_TO_CSV_SUCCESS,
    csvFile,
  };
}

/**
 * Action creator for DOWNLOAD_REQUESTS_TO_CSV_CLEAN.
 * @param csvFile the csv file from the API.
 */
export function downloadRequestsToCSVClean(): IDownloadRequestsToCSVClean {
  return {
    type: "DOWNLOAD_REQUESTS_TO_CSV_CLEAN",
  };
}

export function clearVisualStudioBenefitsFilterValue(): IVisualStudioBenefitsFilterValueClear {
  return {
    type: VISUAL_STUDIO_BENEFITS_FILTER_VALUE_CLEAR,
  };
}

export type VisualStudioBenefitActionTypes =
  | IDownloadRequests
  | IDownloadRequestsSuccess
  | IDownloadRequestsFail
  | IDownloadRequestsToCSVClean
  | ISearchVisualStudioBenefits
  | ISearchVisualStudioBenefitsSuccess
  | ISearchVisualStudioBenefitsFail
  | IUpdateCurrentPage
  | IUpdatePageSize
  | IVisualStudioBenefitsFilterValueAdd
  | IVisualStudioBenefitsFilterValueClear
  | IVisualStudioBenefitsFilterValueRemove
  | IVisualStudioBenefitsFilterValueUpdate;
